import { FeatureTypes } from "entity-app/constants";

// One query for the Required on Site card of a bid package:
//
// 1. governingLinks: follows the backend's governing_link flags down from the
//    bid package so the card can name the last known entity its deadline
//    comes from: BP -> Submittal / Submittal Plan / Material -> Material /
//    Material Plan -> Activity / Onsite Estimate. Three hops covers every
//    chain in the requirement; a successor Material past the first one is
//    ignored anyway.
//
// 2. Linked entities for the link counts and "View All Links", on the same
//    paths as the bid package detail page's Date planner
//    (`bid_package_submittals`, `bid_package_materials` and
//    GET_FEATURE_INSTANCE_PLANNING_DATA):
//      Submittals: BP -> Submittal
//      Materials:  BP -> Submittal Plan -> Material
//      Activities: BP -> Submittal Plan -> Material Plan -> Activity

const GOVERNING_LINKS_WHERE = `
  where: {
    governing_link: { _eq: true }
    deleted: { _eq: false }
    target_feature_instance: { deleted: { _eq: false } }
  }
`;

// Prep/review times come from each milestone's outgoing offset, in milestone
// order, so the offsets are read through the ordered milestones.
// Implicit materials have a UUID title, so they are named after the submittal
// they were created for: the submittal earlier in the chain, else this link.
const ENTITY_FIELDS = `
  id
  number
  title
  auto_inc_value
  feature_type_id
  implicit
  parentSubmittals: links_as_target(
    where: {
      deleted: { _eq: false }
      source_feature_instance: {
        feature_type_id: { _eq: ${FeatureTypes.SUBMITTALS} }
        deleted: { _eq: false }
      }
    }
    limit: 1
  ) {
    source_feature_instance {
      auto_inc_value
    }
  }
  feature_workflow_instance {
    lead_time
    final_deadline_date
    manual_entry_date
  }
  workflow_instance_milestones(order_by: { sequence_no: asc }) {
    sequence_no
    hidden
    feature_workflow_instance_offsets_as_src {
      name
      duration
    }
  }
  feature_activities {
    start_date
    end_date
  }
`;

const governingLinks = (inner = "") => `
  governingLinks: links_as_source(${GOVERNING_LINKS_WHERE}) {
    id
    link_type
    target_feature_instance {
      ${ENTITY_FIELDS}
      ${inner}
    }
  }
`;

const ORDER_BY_TARGET =
  "order_by: { target_feature_instance: { number: asc, auto_inc_value: asc } }";

// Like GET_FEATURE_INSTANCE_PLANNING_DATA, an activity link is one whose
// target carries feature_activities rows. Deleted links and targets are left out.
const ACTIVITY_LINKS = `
  linked_activities: links_as_source(
    where: {
      deleted: { _eq: false }
      target_feature_instance: { deleted: { _eq: false } }
    }
  ) {
    id
    link_type
    target_feature_instance {
      id
      title
      feature_activities {
        start_date
        end_date
      }
    }
  }
`;

const MATERIAL_FIELDS = `
  id
  title
  implicit
  ${ACTIVITY_LINKS}
`;

const LINKED_ENTITIES = `
  bid_package_submittals: links_as_source(
    where: {
      deleted: { _eq: false }
      target_feature_instance: {
        feature_type_id: { _eq: ${FeatureTypes.SUBMITTALS} }
        deleted: { _eq: false }
        implicit: { _eq: false }
      }
    }
    ${ORDER_BY_TARGET}
  ) {
    id
    target_feature_instance {
      id
      title
      auto_inc_value
      ${ACTIVITY_LINKS}
      submittal_materials: links_as_source(
        where: {
          deleted: { _eq: false }
          target_feature_instance: {
            feature_type_id: { _eq: ${FeatureTypes.MATERIALS} }
            deleted: { _eq: false }
          }
        }
        ${ORDER_BY_TARGET}
      ) {
        id
        target_feature_instance {
          ${MATERIAL_FIELDS}
        }
      }
    }
  }
  submittal_plans: links_as_source(
    where: {
      deleted: { _eq: false }
      target_feature_instance: {
        feature_type_id: { _eq: ${FeatureTypes.PLANNING_ONLY_SUBMITTAL} }
        deleted: { _eq: false }
      }
    }
  ) {
    id
    target_feature_instance {
      id
      bid_package_materials: links_as_source(
        where: {
          deleted: { _eq: false }
          target_feature_instance: {
            feature_type_id: { _eq: ${FeatureTypes.MATERIALS} }
            deleted: { _eq: false }
            implicit: { _eq: false }
          }
        }
        ${ORDER_BY_TARGET}
      ) {
        id
        target_feature_instance {
          ${MATERIAL_FIELDS}
        }
      }
      material_plans: links_as_source(
        where: {
          deleted: { _eq: false }
          target_feature_instance: {
            feature_type_id: { _eq: ${FeatureTypes.PLANNING_ONLY_MATERIAL} }
            deleted: { _eq: false }
          }
        }
      ) {
        id
        target_feature_instance {
          id
          ${ACTIVITY_LINKS}
        }
      }
    }
  }
`;

export const QUERY_BID_PACKAGE_REQUIRED_ON_SITE = `BidPackageRequiredOnSite($bidPackageIds: [uuid!]!) {
  feature_instance(where: { id: { _in: $bidPackageIds } }) {
    id
    number
    title
    feature_workflow_instance {
      manual_entry_date
    }
    ${governingLinks(governingLinks(governingLinks()))}
    ${LINKED_ENTITIES}
  }
}`;
